<template>
  <div class="filters-aside" :class="{ 'is-hide': !showFilters }">
    <div class="filters">
      <toggle-buttons
        v-if="hmsFilter"
        :class="hmsFilter.class_name"
        :items="hmsFilter.filter_values"
        :value="selectedFilters[hmsFilter.filter_var] || []"
        @change="filterChange($event, hmsFilter.filter_var)"
      />
      <div v-if="hmsFilter" class="divider" />
      <v-expansion-panels
        v-model="panel"
        class="filters-ordered"
        multiple
      >
        <draggable
          v-model="draggableList"
          class="draggable-wrap"
          handle=".drag-btn"
        >
          <v-expansion-panel
            v-for="(filter, i) in draggableList"
            class="expansion-panel"
            :key="`${filter.filter_var}-${i}`"
          >
            <v-expansion-panel-header class="expansion-header" expand-icon="$vuetify.icons.chevron-down">
              <v-btn class="drag-btn" icon small @click.stop>
                <v-icon size="10">mdi-menu</v-icon>
              </v-btn>
              <span>{{ filter.label }}</span>
              <div
                v-if="selectedFilters[filter.filter_var] && selectedFilters[filter.filter_var].length"
                class="filter-count"
              >
                <span>({{ selectedFilters[filter.filter_var].length === 1 ? '1 item' : selectedFilters[filter.filter_var].length + ' items' }})</span>
                <v-btn icon small @click.stop="filterChange([], filter.filter_var)">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="expansion-content">
              <car-view
                v-if="filter.filter_var === 'car_orientation'"
                :filter="filter"
                :selected="selectedFilters[filter.filter_var] || []"
                @changeView="filterChange"
              />
              <toggle-buttons
                v-else
                :class="filter.class_name"
                :items="filter.filter_values"
                :value="selectedFilters[filter.filter_var] || []"
                @change="filterChange($event, filter.filter_var)"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </draggable>
      </v-expansion-panels>
      <div class="filters-footer">
        <v-btn block outlined @click="handleOpenSettings">
          <v-icon dense class="mr-1">$vuetify.icons.open-window</v-icon>
          Race Settings
        </v-btn>
        <race-settings-dialog ref="raceSettings" />
      </div>
    </div>
    <v-btn
      class="open-modal"
      :class="{ 'is-hide': !showFilters }"
      @click="changeVisibilityFilters"
    >
      <v-icon size="16">{{ showFilters ? '$vuetify.icons.chevron-left' : '$vuetify.icons.chevron-right' }}</v-icon>
    </v-btn>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { HMS_TEAM } from '@/constants'
import CarView from './CarView'
import RaceSettingsDialog from '@/components/dialogs/RaceSettingsDialog'
import ToggleButtons from './ToggleButtons'

export default {
  name: 'FiltersSidebar',
  components: {
    CarView,
    draggable,
    RaceSettingsDialog,
    ToggleButtons,
  },
  data() {
    return {
      panel: [0, 1, 2, 3, 4],
    }
  },
  props: {
    filters: {
      type: Array,
      required: true
    },
    selectedFilters: {
      type: Object,
      required: true
    },
    showFilters: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    draggableList: {
      get() {
        return this.filters.filter(({ order }) => order > 0)
      },
      set(value) {
        const nonordered = this.filters.filter(el => el.order < 0)
        value.forEach((el, i) => el.order = i + 1)
        const newOrder = value.reduce((res, cur) => {
          res[cur.filter_var] = cur.order
          return res
        }, {})
        this.$emit('updateSettings', {
          newOrder,
          updatedFilters: [...value, ...nonordered],
        })
      }
    },
    hmsFilter() {
      return this.filters.find(({ id }) => id === HMS_TEAM);
    },
  },
  methods: {
    changeVisibilityFilters() {
      this.$emit('changeAsideFiltersVisibility')
    },
    filterChange(value, filterTitle) {
      this.$emit('changeFilters', value, filterTitle)
    },
    handleOpenSettings() {
      this.$refs?.raceSettings?.open()
    },
  }
}
</script>

<style lang="scss" scoped>
.filters-aside {
  position: relative;
  width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid var(--v-blue-lighten-3);
  background: white;
  box-shadow: 0px 5px 5px rgba(var(--v-primary-base-rgb), 0.1),
              0px 9px 10px rgba(var(--v-primary-base-rgb), 0.07),
              0px 5px 14px rgba(var(--v-primary-base-rgb), 0.06),
              inset -1px 0px 0px var(--v-blue-lighten-3);
  transition: 0.3s;

  &.is-hide {
    margin-left: -250px;
  }
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding: 14px 16px 0 16px;
  overflow: auto;
}

.filters-ordered {
  gap: 12px;
}

.filters-footer {
  padding: 16px;
  margin-left: -16px;
  margin-right: -16px;
  margin-top: auto;
  background: var(--v-blue-lighten-4);

  button {
    background-color: white;
    color: var(--v-primary-base);
    border-radius: 6px;
    text-transform: capitalize;
  }
}

.draggable-wrap {
  display: contents;
}

.drag-btn {
  height: 16px;
  max-width: 16px;
  position: absolute;
  left: -15px;
  top: 5px;
  opacity: 0;
  transition: 0.3s;
}

.expansion-panel {
  margin-top: 0;

  &::before {
    box-shadow: none;
  }

  &:not(:last-child) {
    padding-bottom: 12px;
    border-bottom: 1px solid var(--v-blue-lighten-2);
  }
}

.expansion-header {
  position: relative;
  min-height: unset;
  padding: 0;
  gap: 10px;
  color: var(--v-primary-base);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;

  &:hover {
    .drag-btn {
      opacity: 1;
    }
  }
}

.expansion-content {
  margin-top: 10px;
}

.filter-count {
  color: var(--v-grey-lighten-1);
  font-weight: 400;
  font-size: 14px;
  text-align: right;
  transform: translateX(4px);

  button {
    width: 20px;
    height: 20px;
    margin-left: 4px;
  }
}

.divider {
  width: 100%;
  height: 1px;
  min-height: 1px;
  background: var(--v-blue-lighten-2);
}

.open-modal {
  position: absolute;
  top: 50%;
  right: 0;
  min-width: 20px!important;
  width: 20px;
  height: 48px!important;
  padding: 0px!important;
  transform: translate(95%, -50%);
  border-radius: 6px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border: 1px solid var(--v-blue-lighten-3);
  border-left: none;
  box-shadow: none;
  background: white!important;
  color: var(--v-primary-base);
  transition: 0.3s;
  z-index: 1;

  &.is-hide {
    width: 36px;
  }
}

::v-deep {
  .filter-car-model {
    button {
      padding: 0 2px!important;
    }

    svg {
      width: unset;
      max-width: 42px;
      height: 16px;
    }
  }

  .filter-car-number {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(30px, 1fr));

    button {
      height: 30px!important;
      font-size: 14px!important;
      text-transform: lowercase!important;
    }
  }

  .filter-hms-team {
    svg {
      height: 20px;
      width: 32px;
    }
  }

  .filter-session {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }

  .v-expansion-panel-content__wrap {
    padding: 0;
  }

  .v-expansion-panel-header__icon {
    order: -1;
    margin-left: 4px;

    svg {
      width: 14px;
    }
  }

  .theme--light.v-expansion-panels .v-expansion-panel:not(:first-child)::after {
    border: none;
  }

  .theme--light.v-btn-toggle:not(.v-btn-toggle--group) .v-btn.v-btn {
    border-color: var(--v-primary-base)!important;
  }
}
</style>
